import { forwardRef, useId } from "react";
import ErrorMessage from "../ErrorMessage/ErrorMessage";
import styles from "./Select.module.scss";
import CaretDownIcon from "../icons/CaretDownIcon";
import clsx from "clsx";

interface ISelect {
  children: React.ReactNode;
  errorMessage?: string;
  icon?: React.ReactNode;
  name: string;
  label: string;
  hideLabel?: boolean;
  description?: string;
  size?: "default" | "compact";
  multiple?: boolean;
}
const Select = forwardRef<HTMLSelectElement | null, ISelect>(
  (
    {
      children,
      errorMessage,
      icon,
      name,
      label,
      hideLabel,
      description,
      size = "default",
      multiple,
      ...rest
    },
    ref,
  ) => {
    const id = useId();
    const descriptionId = useId();
    return (
      <div className={styles.wrapper}>
        {hideLabel ? null : icon ? (
          <div className={styles.labelWithIcon}>
            {icon}
            <label className={styles.label} htmlFor={id}>
              {label}
            </label>
          </div>
        ) : (
          <label className={styles.label} htmlFor={id}>
            {label}
          </label>
        )}
        {description ? (
          <p id={descriptionId} className={styles.description}>
            {description}
          </p>
        ) : undefined}
        {errorMessage ? (
          <div className={styles.errorMessage}>
            <ErrorMessage message={errorMessage} weight="bold" />
          </div>
        ) : undefined}
        <div
          className={clsx([
            styles.selectWrapper,
            styles[`size-${size}`],
            hideLabel && styles.hiddenLabel,
          ])}
        >
          <select
            aria-invalid={Boolean(errorMessage) || undefined}
            aria-describedby={
              description !== undefined ? descriptionId : undefined
            }
            aria-label={hideLabel ? label : undefined}
            id={id}
            name={name}
            {...rest}
            ref={ref}
            className={styles.select}
            multiple={multiple}
          >
            {children}
          </select>
          {!multiple && (
            <div className={styles.arrow}>
              <CaretDownIcon />
            </div>
          )}
        </div>
      </div>
    );
  },
);
Select.displayName = "Select";

const ControlledSelect = ({
  value,
  children,
  onChange,
  label,
  hideLabel,
  size = "default",
}: {
  value: string;
  children: React.ReactNode;
  onChange: (value: string) => void;
  label: string;
  hideLabel?: boolean;
  size?: "default" | "compact";
}) => {
  const id = useId();
  return (
    <div className={styles.wrapper}>
      {hideLabel ? null : (
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
      )}
      <div
        className={clsx([
          styles.controlledSelectWrapper,
          styles[`size-${size}`],
          hideLabel && styles.hiddenLabel,
        ])}
      >
        <select
          id={id}
          aria-label={hideLabel ? label : undefined}
          className={styles.select}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
        >
          {children}
        </select>
        <div className={styles.arrow}>
          <CaretDownIcon />
        </div>
      </div>
    </div>
  );
};

export { ControlledSelect, Select as default };
